import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Car, Calendar, MapPin, Phone, Envelope, Clock, CheckCircle, ArrowRight,
  DownloadSimple, DeviceMobile, Wallet, Info, FileText, QrCode,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const eur = (n) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(n || 0));
const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch (_) { return iso.substring(0, 10); }
};
const formatTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  } catch (_) { return ''; }
};

const STATUS_LABEL = {
  pending: 'Ausstehend', confirmed: 'Bestätigt', active: 'Aktiv', completed: 'Abgeschlossen', cancelled: 'Storniert',
};

const BookingCard = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/cards/${token}`);
        setData(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Buchung nicht gefunden oder Link abgelaufen.');
      } finally { setLoading(false); }
    };
    load();
  }, [token]);

  const icsUrl = `${API}/api/cards/${token}/ics`;
  const qrUrl = `${API}/api/cards/${token}/qr.png?size=10`;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111418] flex items-center justify-center p-4">
        <div className="text-white text-center">
          <div className="w-12 h-12 border-3 border-white/20 border-t-white rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm">Lade Buchungsdetails…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#111418] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-lg p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-[#E60000]/10 mx-auto mb-4 flex items-center justify-center">
            <Info size={28} className="text-[#E60000]" weight="bold" />
          </div>
          <h1 className="text-xl font-bold text-[#111418] mb-2">Buchung nicht gefunden</h1>
          <p className="text-sm text-[#525866]">{error}</p>
        </div>
      </div>
    );
  }

  const b = data.booking;
  const v = data.vehicle;
  const c = data.customer;
  const pickup = data.pickup_station;
  const ret = data.return_station;
  const company = data.company;

  const contractNr = b.contract_number || b.booking_number || (b.id || '').substring(0, 8).toUpperCase();
  const pickupAddress = b.pickup_location || (pickup ? `${pickup.address || ''} ${pickup.city || ''}`.trim() : '');
  const returnAddress = b.return_location || (ret ? `${ret.address || ''} ${ret.city || ''}`.trim() : pickupAddress);
  const mapUrl = pickupAddress ? `https://maps.google.com/?q=${encodeURIComponent(pickupAddress)}` : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#111418] to-[#1F2937] pb-8" data-testid="booking-card-page">
      {/* Header */}
      <div className="px-5 pt-8 pb-6 text-center">
        <p className="text-[10px] uppercase tracking-widest text-white/50 mb-1">{company?.name || 'AutoVermietung'}</p>
        <h1 className="text-white text-lg font-bold">Ihre Booking Card</h1>
      </div>

      {/* Main Card */}
      <div className="mx-4 bg-white rounded-2xl overflow-hidden shadow-2xl">
        {/* Vehicle hero */}
        {v.image_url ? (
          <div className="h-40 bg-[#F1F2F4] relative">
            <img src={v.image_url} alt={`${v.brand} ${v.model}`} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-3 left-4 right-4 text-white">
              <p className="text-lg font-bold leading-tight">{v.brand} {v.model}</p>
              <p className="text-xs opacity-80 font-mono">{v.license_plate}</p>
            </div>
          </div>
        ) : (
          <div className="h-24 bg-[#F1F2F4] flex items-center gap-3 px-5">
            <div className="w-12 h-12 rounded-full bg-[#E60000]/10 flex items-center justify-center">
              <Car size={22} className="text-[#E60000]" weight="fill" />
            </div>
            <div>
              <p className="text-base font-bold text-[#111418] leading-tight">{v.brand} {v.model}</p>
              <p className="text-xs text-[#525866] font-mono">{v.license_plate}</p>
            </div>
          </div>
        )}

        {/* Contract number ribbon */}
        <div className="bg-[#E60000] px-5 py-3 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-white/70">Mietvertragsnummer</p>
            <p className="text-white font-mono font-bold text-lg" data-testid="card-contract-nr">{contractNr}</p>
          </div>
          {b.status && (
            <span className="text-[10px] uppercase tracking-widest bg-white/20 text-white px-2 py-1 rounded-sm">
              {STATUS_LABEL[b.status] || b.status}
            </span>
          )}
        </div>

        {/* Info sections */}
        <div className="p-5 space-y-4">
          {/* Dates */}
          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-full bg-[#009951]/10 flex items-center justify-center shrink-0">
              <Calendar size={18} className="text-[#009951]" weight="bold" />
            </div>
            <div className="flex-1 grid grid-cols-2 gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#8A94A6]">Abholung</p>
                <p className="text-sm font-bold text-[#111418]">{formatDate(b.start_date)}</p>
                <p className="text-xs text-[#525866]">{formatTime(b.start_date)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#8A94A6]">Rückgabe</p>
                <p className="text-sm font-bold text-[#111418]">{formatDate(b.end_date)}</p>
                <p className="text-xs text-[#525866]">{formatTime(b.end_date)}</p>
              </div>
            </div>
          </div>

          {/* Pickup location */}
          {pickupAddress && (
            <div className="flex gap-3">
              <div className="w-10 h-10 rounded-full bg-[#E60000]/10 flex items-center justify-center shrink-0">
                <MapPin size={18} className="text-[#E60000]" weight="bold" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-widest text-[#8A94A6]">Abhol-Adresse</p>
                <p className="text-sm font-semibold text-[#111418]">{pickupAddress}</p>
                {mapUrl && (
                  <a href={mapUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-1 text-xs text-[#E60000] font-bold" data-testid="card-maps-link">
                    In Google Maps öffnen <ArrowRight size={12} weight="bold" />
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Return location (if different) */}
          {returnAddress && returnAddress !== pickupAddress && (
            <div className="flex gap-3">
              <div className="w-10 h-10 rounded-full bg-[#525866]/10 flex items-center justify-center shrink-0">
                <MapPin size={18} className="text-[#525866]" weight="bold" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-widest text-[#8A94A6]">Rückgabe-Adresse</p>
                <p className="text-sm font-semibold text-[#111418]">{returnAddress}</p>
              </div>
            </div>
          )}

          {/* Price */}
          {b.price_info?.total_price != null && (
            <div className="flex gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FFB800]/10 flex items-center justify-center shrink-0">
                <Clock size={18} className="text-[#FFB800]" weight="bold" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-widest text-[#8A94A6]">Gesamtpreis · {b.price_info?.days || 0} Tage</p>
                <p className="text-lg font-bold text-[#111418]" data-testid="card-total-price">{eur(b.price_info.total_price)}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick actions */}
      <div className="mx-4 mt-4 space-y-2">
        {/* QR-Code für schnellen Check-In durch das Personal */}
        <div className="bg-white rounded-lg p-4 shadow" data-testid="card-qr-section">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-[#111418] flex items-center justify-center">
              <QrCode size={18} className="text-white" weight="bold" />
            </div>
            <div>
              <p className="text-sm font-bold text-[#111418]">Check-In QR-Code</p>
              <p className="text-xs text-[#525866]">Bei Abholung dem Personal zeigen — 1 Scan öffnet die Buchung.</p>
            </div>
          </div>
          <div className="flex justify-center bg-[#F7F8FA] rounded-md py-3">
            <img
              src={qrUrl}
              alt="Buchungs-QR-Code"
              className="w-44 h-44 object-contain"
              data-testid="card-qr-image"
            />
          </div>
        </div>

        <a
          href={icsUrl}
          className="flex items-center justify-between bg-white rounded-lg p-4 shadow"
          data-testid="card-ics-link"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#009951]/10 flex items-center justify-center">
              <Calendar size={18} className="text-[#009951]" weight="fill" />
            </div>
            <div>
              <p className="text-sm font-bold text-[#111418]">In Kalender speichern</p>
              <p className="text-xs text-[#525866]">Mit Erinnerung 1 Tag & 2 Std. vorher</p>
            </div>
          </div>
          <DownloadSimple size={20} className="text-[#525866]" />
        </a>

        {/* Apple / Google Wallet placeholder (kommt später mit Zertifikaten) */}
        <div className="bg-white/5 border border-white/10 rounded-lg p-4 text-center" data-testid="card-wallet-placeholder">
          <div className="flex items-center justify-center gap-2 text-white/60 mb-1">
            <Wallet size={16} weight="fill" />
            <p className="text-xs font-bold uppercase tracking-widest">Apple / Google Wallet</p>
          </div>
          <p className="text-[11px] text-white/40">
            Bald verfügbar — dann direkt ins Wallet speicherbar.
          </p>
        </div>
      </div>

      {/* Contact */}
      {(company?.phone || company?.email) && (
        <div className="mx-4 mt-4 bg-white rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-widest text-[#8A94A6] mb-2">Kontakt</p>
          <div className="space-y-1.5">
            {company.phone && (
              <a href={`tel:${company.phone}`} className="flex items-center gap-2 text-sm text-[#111418] font-semibold" data-testid="card-phone">
                <Phone size={14} className="text-[#E60000]" weight="fill" /> {company.phone}
              </a>
            )}
            {company.email && (
              <a href={`mailto:${company.email}`} className="flex items-center gap-2 text-sm text-[#111418] font-semibold" data-testid="card-email">
                <Envelope size={14} className="text-[#E60000]" weight="fill" /> {company.email}
              </a>
            )}
          </div>
        </div>
      )}

      {/* Kundenname & Info */}
      <div className="mx-4 mt-4 text-center">
        <p className="text-white/40 text-[11px]">Buchung für</p>
        <p className="text-white/90 text-sm font-semibold" data-testid="card-customer-name">
          {c.company || `${c.first_name || ''} ${c.last_name || ''}`.trim()}
        </p>
      </div>

      {/* Home-Screen tip */}
      <div className="mx-4 mt-4 flex items-start gap-2 text-white/50 text-[11px] px-4">
        <DeviceMobile size={14} weight="bold" className="mt-0.5" />
        <p>Tipp: Über das Teilen-Menü als &bdquo;Zum Home-Bildschirm hinzufügen&ldquo; speichern — dann ist die Karte immer 1 Tap entfernt.</p>
      </div>
    </div>
  );
};

export default BookingCard;
